/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */

/**
 * Asset loading for the documentation PDF.
 *
 * Parcel's `url:` import scheme resolves screenshots to URLs, but pdfmake needs
 * image bytes as base64 data URLs. Rather than inlining every screenshot into
 * the JavaScript bundle at build time, the images are fetched on demand when
 *  the user asks for the PDF. They are same-origin and typically already in
 *  the browser cache from the Reference page.
 */

/** Data URLs are cached for the lifetime of the page so repeat downloads are cheap. */
const dataUrlCache = new Map<string, string>();

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (): void => resolve(reader.result as string);
    reader.onerror = (): void =>
      reject(reader.error ?? new Error("Unable to read asset contents"));
    reader.readAsDataURL(blob);
  });

/**
 * Fetches a same-origin asset and returns it as a base64 data URL.
 *
 * @throws if the asset cannot be retrieved.
 */
export const fetchAsDataUrl = async (url: string): Promise<string> => {
  const cached = dataUrlCache.get(url);
  if (cached !== undefined) {
    return cached;
  }

  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) {
    throw new Error(`Request for ${url} failed with status ${response.status}`);
  }

  const dataUrl = await blobToDataUrl(await response.blob());
  dataUrlCache.set(url, dataUrl);
  return dataUrl;
};

export interface ResolvedImages {
  /** Source URL to base64 data URL, for every image that loaded successfully. */
  readonly images: ReadonlyMap<string, string>;
  /** Source URLs that could not be retrieved. */
  readonly failed: readonly string[];
}

/**
 * Resolves every image source in parallel.
 *
 * A single unreachable screenshot must not prevent the rest of the document
 * from being produced, so failures are collected and reported rather than
 * thrown. Callers decide how to surface them; the renderer omits any image it
 * cannot resolve.
 */
export const resolveImages = async (
  sources: readonly string[]
): Promise<ResolvedImages> => {
  const results = await Promise.all(
    sources.map(async (source) => {
      try {
        return { source, dataUrl: await fetchAsDataUrl(source) };
      } catch {
        return { source, dataUrl: undefined };
      }
    })
  );

  const images = new Map<string, string>();
  const failed: string[] = [];

  results.forEach(({ source, dataUrl }) => {
    if (dataUrl === undefined) {
      failed.push(source);
    } else {
      images.set(source, dataUrl);
    }
  });

  return { images, failed };
};
